import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Shield,
  FilePlus2,
  Users,
  BarChart3
} from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Correo o contraseña incorrectos'
      );
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: FilePlus2,
      title: 'Registra',
      description: 'nuevas solicitudes',
      color: '#2563eb'
    },
    {
      icon: Users,
      title: 'Asigna y da',
      description: 'seguimiento',
      color: '#22c55e'
    },
    {
      icon: BarChart3,
      title: 'Mejora y optimiza',
      description: 'procesos',
      color: '#2563eb'
    }
  ];

  return (
    <>
      <style>
        {`
          * {
            box-sizing: border-box;
          }
          body {
            margin: 0;
          }
          .sf-login {
            min-height: 100vh;
            background: #f5f7fa;
            font-family: "Segoe UI", Arial, sans-serif;
            position: relative;
            overflow: hidden;
          }

          /* =====================================================
             CONTENEDOR PRINCIPAL
          ===================================================== */

          .sf-main {
            min-height: 100vh;
            width: 100%;
            max-width: 1280px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: 1fr 560px;
            align-items: center;
            gap: 70px;
            padding: 45px 70px 80px;
            position: relative;
            z-index: 2;
          }

          /* =====================================================
             PANEL IZQUIERDO
          ===================================================== */

          .sf-brand {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
          }

          .sf-main-logo {
            width: 4000px;
            max-height: 300px;
            object-fit: contain;
            margin-bottom: 12px;
          }
                    
          /* =====================================================
             ICONOS DE FUNCIONES
          ===================================================== */

          .sf-features {
            width: 100%;
            display: flex;
            justify-content: center;
            gap: 45px;
          }

          .sf-feature {
            width: 125px;
            text-align: center;
          }

          .sf-feature-icon {
            width: 58px;
            height: 58px;
            margin: 0 auto 12px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            box-shadow:
              0 7px 20px rgba(37, 99, 235, .18);
          }

          .sf-feature-title {
            font-size: 15px;
            color: #172b4d;
            font-weight: 700;
            margin-bottom: 3px;
          }

          .sf-feature-description {
            font-size: 13px;
            color: #64748b;
            line-height: 1.35;
          }

          /* =====================================================
             DESARROLLADO POR SOPORTEALX
          ===================================================== */

          .sf-developed {
            margin-top:10px;
            width: 100%;
            text-align: center;
            font-size: 13px;
            color: #64748b;
            letter-spacing: .2px;
          }

          .sf-developed strong {
            color: #1a2f6b;
            font-weight: 700;
          }

          /* =====================================================
             COLUMNA DERECHA          
             IMPORTANTE:
             La tarjeta y el footer están dentro de esta columna.
             Por eso ambos quedan perfectamente alineados.
          ===================================================== */

          .sf-login-column {
            width: 560px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
          }

          /* =====================================================
             TARJETA LOGIN
          ===================================================== */

          .sf-card {
            width: 100%;
            min-height: 450px;
            background: rgba(255,255,255,.98);
            border-radius: 24px;
            padding: 50px 58px;
            box-shadow:
              0 20px 50px rgba(15,23,42,.10),
              0 3px 10px rgba(15,23,42,.04);
            position: relative;
            overflow: hidden;
          }

          /* PUNTOS SUPERIORES */

          .sf-dots {
            position: absolute;
            top: 25px;
            right: 25px;
            width: 120px;
            height: 90px;
            opacity: .18;
            background-image:
              radial-gradient(
                #2563eb 2px,
                transparent 2px
              );
            background-size: 15px 15px;
          }

          .sf-card-content {
            position: relative;
            z-index: 5;
          }

          .sf-card-logo {
            width: 82px;
            height: 82px;
            object-fit: contain;
            display: block;
            margin: 0 auto 16px;
          }

          .sf-card-title {
            text-align: center;
            margin: 0;
            color: #071a3a;
            font-size: 30px;
            font-weight: 800;
            line-height: 1.35;
            font-Family: lora;
          }

          .sf-card-subtitle {
            text-align: center;
            margin: 10px 0 38px;
            color: #64748b;
            font-size: 15px;
          }

          /* =====================================================
             FORMULARIO
          ===================================================== */

          .sf-field {
            margin-bottom: 20px;
          }

          .sf-label {
            display: block;
            margin-bottom: 8px;
            font-size: 14px;
            font-weight: 600;
            color: #172033;
          }

          .sf-input-wrapper {
            height: 54px;
            border: 1.5px solid #d9e0e8;
            border-radius: 10px;
            display: flex;
            align-items: center;
            padding: 0 15px;
            background: #fff;
            transition: all .2s ease;
          }

          .sf-input-wrapper:focus-within {
            border-color: #2563eb;
            box-shadow:
              0 0 0 3px rgba(37,99,235,.08);
          }

          .sf-input-icon {
            color: #718096;
            flex-shrink: 0;
          }

          .sf-input {
            width: 100%;
            height: 100%;
            border: none;
            outline: none;
            padding: 0 12px;
            font-family: inherit;
            font-size: 15px;
            color: #1e293b;
            background: transparent;
          }

          .sf-input::placeholder {
            color: #9ca9ba;
          }

          /* =====================================================
             MOSTRAR CONTRASEÑA
          ===================================================== */

          .sf-eye {
            border: none;
            background: transparent;
            padding: 5px;
            cursor: pointer;
            color: #718096;
            display: flex;
            align-items: center;
          }

          /* =====================================================
             ERROR
          ===================================================== */

          .sf-error {
            background: #fff1f2;
            color: #c62828;
            border: 1px solid #fecdd3;
            border-radius: 9px;
            padding: 11px 14px;
            margin-bottom: 17px;
            text-align: center;
            font-size: 13px;
          }

          /* =====================================================
             BOTÓN
          ===================================================== */

          .sf-submit {
            width: 100%;
            height: 55px;
            border: none;
            border-radius: 10px;
            background: linear-gradient(
              90deg,
              #2563eb 0%,
              #1769e8 100%
            );
            color: white;
            font-family: inherit;
            font-size: 16px;
            font-weight: 700;
            cursor: pointer;
            transition: all .2s ease;
            box-shadow:
              0 7px 18px rgba(37,99,235,.20);
          }

          .sf-submit:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow:
              0 10px 22px rgba(37,99,235,.28);
          }

          .sf-submit:disabled {
            opacity: .65;
            cursor: not-allowed;
          }

          /* =====================================================
             RECORDAR / CONTRASEÑA
          ===================================================== */

          .sf-options {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 20px;
            font-size: 13px;
          }

          .sf-remember {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #64748b;
            cursor: pointer;
          }

          .sf-remember input {
            width: 17px;
            height: 17px;
            accent-color: #2563eb;
          }

          .sf-forgot {
            color: #2563eb;
            font-weight: 600;
            cursor: pointer;
          }

          .sf-forgot:hover {
            text-decoration: underline;
          }
        
          /* =====================================================
             ONDAS DECORATIVAS
          ===================================================== */

          .sf-wave {
            position: absolute;
            left: 34%;
            right: -100px;
            bottom: -120px;
            height: 310px;
            z-index: 1;
            pointer-events: none;
          }

          .sf-wave-blue {
            position: absolute;
            width: 900px;
            height: 330px;
            border-radius: 50% 50% 0 0;
            background: linear-gradient(
              135deg,
              #2563eb 0%,
              #163b75 55%,
              #172b4d 100%
            );
            transform: rotate(-8deg);
            bottom: -180px;
            left: 0;
          }

          .sf-wave-green {
            position: absolute;
            width: 600px;
            height: 190px;
            border-radius: 50%;
            background: linear-gradient(
              90deg,
              #22c55e,
              #8bd63e
            );
            transform: rotate(-12deg);
            right: -80px;
            bottom: 80px;
          }

          /* =====================================================
             RESPONSIVE
          ===================================================== */

          @media (max-width: 1000px) {

            .sf-main {
              grid-template-columns: 1fr;
              padding: 35px 25px 90px;
              max-width: 650px;
            }

            .sf-brand {
              display: none;
            }

            .sf-login-column {
              width: 100%;
            }

            .sf-card {
              min-height: auto;
            }

            .sf-wave {
              left: 0;
            }

          }

          @media (max-width: 600px) {

            .sf-main {
              padding: 20px 15px 75px;
            }

            .sf-card {
              padding: 38px 25px;
              border-radius: 18px;
            }

            .sf-card-title {
              font-size: 25px;
            }

            .sf-card-logo {
              width: 70px;
              height: 70px;
            }

            .sf-options {
              gap: 15px;
              flex-wrap: wrap;
            }

            .sf-footer {
              font-size: 12px;
              gap: 8px;
            }
          }
        `}
      </style>

      <div className="sf-login">
        <main className="sf-main">
          {/* ===================================================
              PANEL IZQUIERDO
          =================================================== */}

          <section className="sf-brand">
            <img
              src="/Logo_Login.png"
              alt="ServiceFlow"
              className="sf-main-logo"
            />
            {/* ICONOS */}
            <div className="sf-features">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div
                    className="sf-feature"
                    key={feature.title}
                  >
                    <div
                      className="sf-feature-icon"
                      style={{
                        background: feature.color
                      }}
                    >
                      <Icon
                        size={27}
                        strokeWidth={2}
                      />
                    </div>

                    <div className="sf-feature-title">
                      {feature.title}
                    </div>

                    <div className="sf-feature-description">
                      {feature.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ===================================================
              COLUMNA DEL LOGIN
          =================================================== */}

          <div className="sf-login-column">
            {/* TARJETA */}
            <section className="sf-card">
              <div className="sf-dots" />
              <div className="sf-card-content">
               <h2 className="sf-card-title">
                  Gestión y seguimiento de
                  <br />
                  servicios internos
                </h2>
                <p className="sf-card-subtitle">
                  Inicia sesión para continuar
                </p>

                <form onSubmit={handleSubmit}>
                  {/* EMAIL */}
                  <div className="sf-field">
                    <label className="sf-label">
                      Correo electrónico
                    </label>
                    <div className="sf-input-wrapper">
                      <Mail
                        size={20}
                        className="sf-input-icon"
                      />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) =>
                          setEmail(e.target.value)
                        }
                        required
                        placeholder="correo@empresa.com"
                        className="sf-input"
                      />
                    </div>
                  </div>

                  {/* CONTRASEÑA */}
                  <div className="sf-field">
                    <label className="sf-label">
                      Contraseña
                    </label>
                    <div className="sf-input-wrapper">
                      <Lock
                        size={20}
                        className="sf-input-icon"
                      />
                      <input
                        type={
                          showPassword
                            ? 'text'
                            : 'password'
                        }
                        value={password}
                        onChange={(e) =>
                          setPassword(e.target.value)
                        }
                        required
                        placeholder="••••••••"
                        className="sf-input"
                      />
                      <button
                        type="button"
                        className="sf-eye"
                        onClick={() =>
                          setShowPassword(
                            !showPassword
                          )
                        }
                        aria-label={
                          showPassword
                            ? 'Ocultar contraseña'
                            : 'Mostrar contraseña'
                        }
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* ERROR */}
                  {error && (
                    <div className="sf-error">
                      {error}
                    </div>
                  )}

                  {/* BOTÓN */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="sf-submit"
                  >
                    {loading
                      ? 'Iniciando sesión...'
                      : 'Iniciar sesión'}
                  </button>

                  {/* OPCIONES */}
                  <div className="sf-options">
                    <label className="sf-remember">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) =>
                          setRemember(
                            e.target.checked
                          )
                        }
                      />
                      Recordarme
                    </label>
                    <span className="sf-forgot">
                      ¿Olvidaste tu contraseña?
                    </span>
                  </div>
                </form>
              </div>
            </section>

            {/* =================================================
                FOOTER DEL LOGIN
            ================================================= */}

           <div className="sf-developed">
              Sistema desarrollado por{' '}
              <strong>
                SoporteALX
              </strong>
            </div>
          </div>
        </main>

        {/* =====================================================
            ONDAS DEL FONDO
        ===================================================== */}

        <div className="sf-wave">
          <div className="sf-wave-green" />
          <div className="sf-wave-blue" />
        </div>
      </div>
    </>
  );
}